import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wrench, Loader2, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

/**
 * Tiny admin maintenance tile — exposes one-click actions for the
 * "self-healing" endpoints we keep in the backend. Each is idempotent.
 */
const AdminMaintenanceTile = () => {
  const [healing, setHealing] = useState(false);
  const [healResult, setHealResult] = useState(null);
  const [digesting, setDigesting] = useState(false);

  const handleSendDigest = async () => {
    setDigesting(true);
    const { data, error } = await apiFetch('/api/admin/admin-digest/run-now', { method: 'POST' });
    setDigesting(false);
    if (error) toast.error(error);
    else if (data?.sent) {
      const c = data.counts || {};
      toast.success(`Digest sent to ${(data.recipients || []).length} recipient(s) — ${c.blog || 0} posts, ${c.submissions || 0} submissions, ${c.contributors || 0} contributors`);
    } else if (data?.reason === 'empty') {
      toast.success('Nothing pending — digest skipped on purpose.');
    } else {
      toast.error('Digest send failed — check server logs.');
    }
  };

  const handleHealCovers = async () => {
    setHealing(true);
    setHealResult(null);
    const { data, error } = await apiFetch('/api/blog/admin/covers/heal', { method: 'POST' });
    setHealing(false);
    if (error) toast.error(error);
    else {
      setHealResult(data);
      const fixed = (data?.rewritten_legacy_paths || 0) + (data?.backfilled_from_blog_covers || 0);
      toast.success(
        fixed === 0
          ? 'No broken covers found — you\'re all good!'
          : `Healed ${fixed} cover${fixed === 1 ? '' : 's'} ✨`
      );
    }
  };

  return (
    <Card data-testid="admin-maintenance-tile">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Wrench className="h-4 w-4 text-[#7C3AED]" />
          Maintenance
        </CardTitle>
        <CardDescription className="text-xs">
          One-click fixers for known data-integrity issues. Safe to re-run any time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-start justify-between gap-3 p-3 rounded-md border">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Heal broken blog covers</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Rewrites legacy <code className="text-[10px]">/blog-covers/...</code> URLs to MongoDB-backed
              paths that survive every redeploy, and backfills covers for posts that have a stored image
              but no <code className="text-[10px]">cover_image</code> field set.
            </p>
            {healResult && (
              <p className="text-[11px] text-[#00A896] mt-1 flex items-center gap-1">
                <Check className="h-3 w-3" />
                Last run: rewrote {healResult.rewritten_legacy_paths}, backfilled {healResult.backfilled_from_blog_covers}, skipped {healResult.skipped_no_matching_cover_doc} missing
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleHealCovers}
            disabled={healing}
            data-testid="heal-covers-btn"
          >
            {healing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Healing…</> : <><Wrench className="h-4 w-4 mr-1" /> Run</>}
          </Button>
        </div>

        <div className="flex items-start justify-between gap-3 p-3 rounded-md border">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Send admin moderation digest now</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Manually fires the same email the scheduler sends every morning at 9:00 AM (America/Chicago).
              Skipped automatically when nothing is pending. Editable in <em>Email Templates → admin_moderation_digest</em>.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSendDigest}
            disabled={digesting}
            data-testid="send-admin-digest-btn"
          >
            {digesting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending…</> : <><Mail className="h-4 w-4 mr-1" /> Send</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminMaintenanceTile;
